'use client'

import { useEffect, useState } from 'react'
import { createContext, useContext } from 'use-context-selector'
import type { FC, ReactNode } from 'react'
import Cookies from 'js-cookie'
import type { ResourceContextValue } from './resource-context'
import { useResourceContext } from './resource-context'
import type { ProjectScopeType } from '@/fe-common'
import _ from 'lodash'
import { getPermissionResult } from '@/models/permission'
import type { PermissionSet, UserPermissionSet } from '@/models/permission'
import { fetchUserPermissions } from '@/service/permission'
import { useCurrentProject } from '@/fe-common/hooks/user'
import { isTKR } from '@/config'
import AppContext from '@/context/app-context'

export type PermissionContextValue = {
  permissions: Partial<UserPermissionSet>
  isAdminMode: boolean
  isDomainMode: boolean
}

const initUserPermissionSet: UserPermissionSet = {}

export const PermissionContext = createContext<PermissionContextValue>({
  permissions: initUserPermissionSet,
  isAdminMode: false,
  isDomainMode: false,
})

export type PermissionContextProviderProps = {
  children: ReactNode
}

export const PermissionContextProvider: FC<PermissionContextProviderProps> = ({
  children,
}) => {
  const [currentScope, setCurrentScope] = useState<ProjectScopeType>()
  const [permissions, setUserPermissions] = useState<UserPermissionSet>(
    initUserPermissionSet,
  )

  const { isAdminMode, isDomainMode } = useCurrentProject()

  useEffect(() => {
    setCurrentScope(Cookies.get('scope') as ProjectScopeType)
  }, [])

  useEffect(() => {
    if (currentScope) {
      fetchUserPermissions(currentScope).then(res => {
        setUserPermissions(res)
      })
    }
  }, [currentScope])

  return (
    <PermissionContext.Provider
      value={{
        permissions,
        isAdminMode,
        isDomainMode,
      }}>
      {children}
    </PermissionContext.Provider>
  )
}

export const usePermissionContext = () => useContext(PermissionContext)

export function usePermission(
  expectPermission: PermissionSet,
  adminOnly = false,
) {
  const { permissions, isAdminMode, isDomainMode } = usePermissionContext()
  const scopedResources = useResourceContext()
  if (isTKR) return true

  return hasPermission(
    expectPermission,
    adminOnly,
    permissions,
    isAdminMode,
    isDomainMode,
    scopedResources,
  )
}

export function hasPermission(
  expectPermission: PermissionSet | undefined,
  adminOnly = false,
  permissions: Partial<UserPermissionSet>,
  isAdminMode: boolean,
  isDomainMode: boolean,
  scopedResources: ResourceContextValue,
) {
  if (!expectPermission) {
    if (adminOnly) return isAdminMode
    return true
  }

  function currentScopeResources() {
    const ret = [...scopedResources.ALL_RESOURCES]
    const systemResource = scopedResources && scopedResources.system
    const domainResource = scopedResources && scopedResources.domain
    // 如果为管理后台返回所有的资源
    if (isAdminMode) {
      return ret
    }
    // 如果为域管理后台返回所有资源减去管理的资源
    if (isDomainMode) {
      _.remove(ret, resource => {
        return systemResource && systemResource.includes(resource)
      })
      return ret
    }
    // 如果为普通后台减去管理资源和域资源
    _.remove(ret, resource => {
      return (
        (systemResource && systemResource.includes(resource)) ||
        (domainResource && domainResource.includes(resource))
      )
    })
    return ret
  }
  const currentResources = currentScopeResources()

  const pArr = permissions[expectPermission]

  if (!pArr || !pArr.length) {
    return false
  }
  const resource = pArr[1]

  if (!currentResources.includes(resource)) {
    return false
  }
  if (getPermissionResult(permissions, expectPermission) === 'deny') {
    return false
  }

  return true
}

export const VoidPermissionContextProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const { userProfile } = useContext(AppContext)
  const isAdmin = !!userProfile?.is_super

  return (
    <PermissionContext.Provider
      value={{ permissions: {}, isAdminMode: isAdmin, isDomainMode: false }}>
      {children}
    </PermissionContext.Provider>
  )
}

export default PermissionContext
